import { getLogger } from '@logtape/logtape';
import { createElement } from 'react';

import { CollectionSuccessNotification } from '@/components/collection-success-notification.tsx';

import { Appointment, AppointmentCancelReason, CollectionActionScope } from '@/models/appointment/Appointment.ts';
import { CancelAppointmentOptions } from '@/models/appointment/CancelAppointmentOptions.ts';

import {
    CancelledItem,
    cancelAppointment,
    cancelAppointmentsCollection,
    updateAppointment,
} from '@/services/appointmentService.ts';
import { showErrorNotification, showSuccessNotification } from '@/services/notificationService.ts';

import { getInstanceId, isChainMode, isSeriesMode } from '@/utils/appointmentUtils.ts';

const logger = getLogger(['hook', 'cancel-appointment']);

type CancelMode = 'series' | 'chain' | 'single';

const CANCEL_ERROR_MESSAGES: Record<CancelMode, string> = {
    series: 'Fehler beim Löschen der Terminserie. Bitte versuchen Sie es später erneut.',
    chain: 'Fehler beim Stornieren der Terminkette. Bitte versuchen Sie es später erneut.',
    single: 'Bei der Stornierung des Termins ist ein Problem aufgetreten. Bitte versuchen Sie es später erneut.',
};

const CANCEL_SUCCESS_MESSAGES: Record<'series' | 'chain', string> = {
    series: 'Alle Termine der Serie gelöscht.',
    chain: 'Alle Termine der Kette storniert.',
};

const getCancelMode = (appointment: Appointment, scope: CollectionActionScope | undefined): CancelMode => {
    if (isSeriesMode(appointment) && scope === CollectionActionScope.ALL_FUTURE) return 'series';
    if (isChainMode(appointment) && scope === CollectionActionScope.ALL_FUTURE) return 'chain';
    return 'single';
};

const showCollectionSuccessNotification = (cancelledItems: CancelledItem[], mode: 'series' | 'chain'): void => {
    showSuccessNotification(
        createElement(CollectionSuccessNotification, { appointments: cancelledItems, showDeleted: true }),
        CANCEL_SUCCESS_MESSAGES[mode]
    );
};

const cancel = async (
    appointment: Appointment,
    scope: CollectionActionScope | undefined,
    cancelOption: CancelAppointmentOptions
): Promise<boolean> => {
    const customerId = appointment.customer_id ?? '';
    const instanceId = getInstanceId(appointment) ?? '';
    const mode = getCancelMode(appointment, scope);

    try {
        if (cancelOption === CancelAppointmentOptions.NO_SHOW) {
            const [success, message] = await updateAppointment(appointment, {
                appointment: { is_no_show: true },
            });
            if (!success) {
                showErrorNotification(message);
                return false;
            }
            return true;
        }

        const reason: AppointmentCancelReason = AppointmentCancelReason[cancelOption];
        if (mode === 'single') {
            await cancelAppointment(customerId, instanceId, appointment, reason);
            showSuccessNotification('Termin wurde gelöscht');
        } else {
            const cancelledItems = await cancelAppointmentsCollection(
                customerId,
                instanceId,
                appointment.collection_id,
                reason
            );
            showCollectionSuccessNotification(cancelledItems, mode);
        }

        return true;
    } catch (error) {
        logger.error('Error while cancelling appointment', { error: error });
        showErrorNotification(CANCEL_ERROR_MESSAGES[mode]);
        return false;
    }
};

interface UseCancelAppointmentReturn {
    cancel: (
        appointment: Appointment,
        scope: CollectionActionScope | undefined,
        cancelOption: CancelAppointmentOptions
    ) => Promise<boolean>;
}

export const useCancelAppointment = (): UseCancelAppointmentReturn => {
    return { cancel };
};
